// src/App.tsx
import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider, CssBaseline, Box } from '@mui/material';
import Cookies from 'js-cookie';

// Pages
import Vehicles from './pages/Vehicles';
import About from './pages/About';
import Login from './pages/Login';
import VehicleScreen from './pages/Vehicle';

// Components
import Navbar from './components/Navbar';
import IFrame from './pages/IFrame';

// Services
import AccountPage from './pages/Account';
import ResetPassword from './components/authentication/ForgotPasswordContainer';
import Logout from './pages/Logout';
import UploadDialogWrapper from './components/vehicles/FileUploading/UploadDialogWrapper';
import Redirect from './pages/Redirect';
import Billing from './pages/Billing';
import Reporting from './pages/Reporting';
import ClientDashboard from './pages/ClientDashboard';
import CsvDownloadPage from './pages/CsvDownloadPage';
import Systems from './pages/Systems';

const AppContent: React.FC<{
  darkMode: boolean;
  toggleDarkMode: () => void;
}> = ({ darkMode, toggleDarkMode }) => {
  const location = useLocation();
  const isIframe = location.pathname.includes('/iframe');

  return (
    <Box sx={{ width: "100vw", height: "100vh", overflow: 'hidden' }}>
      {!isIframe && (
        <Navbar
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
        />
      )}
      <Box sx={{ marginTop: isIframe ? 0 : '64px' }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/resetPassword" element={<ResetPassword />} />

          {/* Iframe Routes - No authentication required */}
          <Route path="/iframe/:clientId/:assetId" element={<IFrame />} />
          <Route path="/iframe/:clientId/:assetId/:image_type" element={<IFrame />} />
          <Route path="/iframe/*" element={<Navigate to="/" replace />} />

          {/* Protected Routes */}
          <Route path="/" element={<Redirect />} />
          <Route path="/vehicles" element={<Vehicles />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/reporting" element={<Reporting />} />
          <Route path="/client-dashboard" element={<ClientDashboard />} />
          <Route path="/systems" element={<Systems />} />
          <Route path="/about" element={<About />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/vehicle/:clientId/:assetId" element={<VehicleScreen />} />
          <Route path="/vehicle/:clientId/:assetId/edit" element={<UploadDialogWrapper />} />
          <Route path="/csv-download" element={<CsvDownloadPage />} />


          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>
    </Box>
  );
};

const App: React.FC = () => {
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const cookieValue = Cookies.get('darkMode');
    return cookieValue === 'true'; // default to false if not set
  });


  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#000' : '#fff',
      },
      action: {
        selected: darkMode ? '#3c3c3c' : '#fff',
      },
      background: {
        default: darkMode ? '#333' : '#ddd',
        paper: darkMode ? 'black' : '#fff',
      },
      text: {
        primary: darkMode ? '#fff' : '#111',
      },
    },
    components: {
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: darkMode ? '#fff' : '#000',
            '&.Mui-focused': {
              color: darkMode ? '#fff' : '#000',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: darkMode ? '#fff' : '#000',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? '#fff' : '#000',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? '#fff' : '#000',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? '#fff' : '#000',
            },
          },
        },
      },
    },
  });

  const toggleDarkMode = () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    Cookies.set('darkMode', newValue.toString(), { expires: 365 });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
        />
      </Router>
    </ThemeProvider>
  );
};

export default App;
